import { useEffect, useState } from "react";
import { RANKING_MAX_ROWS } from "../lib/ranking";

/**
 * Paginação progressiva para tabelas de ranking.
 *
 * As listas do BI chegam INTEIRAS da API (a ordenação é do servidor, para o
 * ranking ser o mesmo no painel e no WhatsApp). O corte é só de exibição: mostrar
 * 75 placas de uma vez esconde o topo da lista atrás do scroll, que é justamente o
 * que a pessoa abriu a tela para ver.
 *
 * Estado no HOOK e não no componente porque quem fatia é a página — o componente
 * só desenha o rodapé.
 */

/** Quantas linhas aparecem antes do primeiro clique, e a cada clique seguinte. */
export const PAGE_SIZE = 10;

export interface ProgressiveList<T> {
  /** as linhas que devem ser renderizadas agora */
  visible: T[];
  /** total disponível (para o "mostrando X de Y") */
  total: number;
  /** ainda há o que carregar? */
  hasMore: boolean;
  /** mostra mais uma página */
  loadMore: () => void;
  /** mostra tudo de uma vez */
  loadAll: () => void;
}

/**
 * Fatia a lista e devolve o controle da paginação.
 *
 * O contador volta ao início quando a LISTA MUDA (troca de período, de filtro):
 * sem isso, alguém que carregou 50 linhas em junho abriria julho já com 50
 * linhas expandidas, o que não é o estado inicial que a tela promete.
 */
export function useProgressiveList<T>(items: T[], pageSize = PAGE_SIZE): ProgressiveList<T> {
  const [count, setCount] = useState(pageSize);

  // `items.length` como dependência cobre a troca de período; a identidade do
  // array muda a cada refetch do react-query, e reagir a ela zeraria a
  // paginação em toda revalidação de fundo.
  useEffect(() => { setCount(pageSize); }, [items.length, pageSize]);

  return {
    visible: items.slice(0, count),
    total: items.length,
    hasMore: count < items.length,
    loadMore: () => setCount((c) => c + pageSize),
    loadAll: () => setCount(items.length),
  };
}

interface Props {
  list: ProgressiveList<unknown>;
  /** substantivo no plural para o texto ("placas", "clientes") */
  label?: string;
}

/**
 * Conteúdo do rodapé, sem o invólucro. Existe porque o BI tem ranking em TABELA
 * e ranking em lista de cards, e um `<tr>` dentro de uma `<div>` (ou vice-versa)
 * é HTML inválido — o navegador reposiciona o elemento e o rodapé some da tela.
 */
function LoadMoreContent({ list, label = "itens" }: Props) {
  return (
    <div className="flex items-center justify-between gap-4 flex-wrap">
      <span className="text-xs text-gray-500">
        Mostrando <strong className="text-gray-700">{list.visible.length}</strong> de{" "}
        <strong className="text-gray-700">
          {list.total}{list.total >= RANKING_MAX_ROWS ? "+" : ""}
        </strong>{" "}
        {label}
        {/* No teto, o total real é desconhecido — dizer "100" seria afirmar o que
            não se sabe. O "+" e o aviso deixam explícito que é o topo da lista. */}
        {list.total >= RANKING_MAX_ROWS && (
          <span className="text-gray-400"> · topo do ranking</span>
        )}
      </span>
      {list.hasMore && (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={list.loadMore}
            className="px-3 py-1.5 text-xs font-medium rounded-md border border-surface-border text-tm-700 hover:bg-white transition-colors"
          >
            Carregar mais {Math.min(PAGE_SIZE, list.total - list.visible.length)}
          </button>
          <button
            type="button"
            onClick={list.loadAll}
            className="px-3 py-1.5 text-xs font-medium rounded-md text-gray-500 hover:text-gray-700 transition-colors"
          >
            Ver {label === "itens" ? "todos" : "todas"} ({list.total}{list.total >= RANKING_MAX_ROWS ? "+" : ""})
          </button>
        </div>
      )}
    </div>
  );
}

/** Rodapé para ranking em TABELA — ocupa a linha inteira. */
export function LoadMoreRow({ list, colSpan, label }: Props & { colSpan: number }) {
  if (list.total === 0) return null;
  return (
    <tr className="border-t border-surface-border bg-gray-50/60">
      <td colSpan={colSpan} className="px-5 py-3">
        <LoadMoreContent list={list} label={label} />
      </td>
    </tr>
  );
}

/** Rodapé para ranking em LISTA de cards (divs). Mesmo conteúdo, outro invólucro. */
export function LoadMoreBar({ list, label }: Props) {
  if (list.total === 0) return null;
  return (
    <div className="px-5 py-3 bg-gray-50/60">
      <LoadMoreContent list={list} label={label} />
    </div>
  );
}
